import { PayloadAction } from '@reduxjs/toolkit';
import * as E from 'fp-ts/lib/Either';
import * as t from 'io-ts';
import reporter from 'io-ts-reporters';
import { parse, ParseResult } from 'papaparse';
import { call, ForkEffect, put, select, takeLatest } from 'redux-saga/effects';
import { getCustomAsyncActions } from 'src/store/asyncAction';
import { selectModelId } from '../../selectors';
import { saveModelCompositions } from '../../services';
import {
  AllocationsImporterResponse,
  ImportRequestPayload,
  ModelCompositionMethod,
  ModelCompositionType,
  SaveModelCompositionPayload,
  SaveModelCompositionResult,
  WeightedAssetAllocation,
} from '../../types';
import { checkDuplicateSecurityCodes, targetAllocationsShouldBeBetween0And1 } from '../rules';

export const importWeightedAllocationsActionType = '@@model/composition/allocations/weightedModelImport';
export const importWeightedAllocationsAction = getCustomAsyncActions<
  ImportRequestPayload,
  AllocationsImporterResponse,
  string[]
>(importWeightedAllocationsActionType);

const allocation: t.Type<WeightedAssetAllocation> = t.type({
  securityCode: t.string,
  targetAllocation: t.number,
});

const decodeData = (data: WeightedAssetAllocation[]) => {
  const errors: string[] = [];
  const allocations: WeightedAssetAllocation[] = [];
  console.debug('Decoding file ...');
  data.forEach((row, index) => {
    const result = allocation.decode(row);

    if (E.isLeft(result)) {
      const error = reporter.report(result);
      errors.push(`row: ${index + 1} message: ${error.toString()}\n`);
    } else {
      allocations.push({
        securityCode: result.right.securityCode,
        targetAllocation: result.right.targetAllocation,
      });
    }
  });
  return { allocations, errors };
};

const validateData = (
  data: WeightedAssetAllocation[]
): { allocations: WeightedAssetAllocation[]; errors: string[] } => {
  const errors: string[] = [] as string[];
  const allocations: WeightedAssetAllocation[] = data;

  // has duplicate security codes
  const duplicateErrors = checkDuplicateSecurityCodes(data);
  if (duplicateErrors !== '') {
    return { allocations: [], errors: [duplicateErrors] };
  }
  // target allocations must be more than 0 and less than 1
  const targetAllocationsErrors = targetAllocationsShouldBeBetween0And1(data);
  if (targetAllocationsErrors !== '') {
    return { allocations: [], errors: [targetAllocationsErrors] };
  }
  return { allocations, errors };
};

function parseImportFile(importFile: File) {
  return new Promise<ParseResult<unknown>>((resolve) => {
    parse(importFile, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      transformHeader: function (header) {
        return convertToCamelCase(header);
      },
      complete: function (results) {
        resolve(results);
      },
    });
  });
}

function* importAllocations(action: PayloadAction<ImportRequestPayload>) {
  try {
    console.debug('Weighted model import ...');

    // file should have CSV extension
    const extension = action.payload.file.name.split('.').pop();
    if (extension && extension.toLowerCase().indexOf('csv') < 0) return `row: The file loaded is not a CSV.\n`;

    const result: ParseResult<unknown> = yield call(parseImportFile, action.payload.file);

    if (result.meta.fields) {
      // template must be exaactly two columns
      if (result.meta.fields.length != 2) {
        console.debug('Import file meta fields:', result.meta.fields);
        return `row: There are extra rows or columns in the file loaded.\n`;
      }
      // must contain only "securityCode" and "targetAllocation" columns
      if (result.meta.fields[0] != 'securityCode' && result.meta.fields[1] != 'targetAllocation') {
        console.debug('Import file meta fields:', result.meta.fields);
        return `row: Header rows do not match supported template.\n`;
      }
    }

    // failed parsing
    if (result.errors && result.errors.length > 0) {
      const parseErrors = result.errors.map((error) => {
        return `row: ${error.row} type: ${error.type} code: ${error.code} message: ${error.message}\n`;
      });
      const response: PayloadAction<string[]> = yield put(importWeightedAllocationsAction.rejected(parseErrors));
      return response;
    }

    // decode data
    const decodedData = decodeData(result.data as WeightedAssetAllocation[]);
    if (decodedData.errors && decodedData.errors.length > 0) {
      const response: PayloadAction<string[]> = yield put(importWeightedAllocationsAction.rejected(decodedData.errors));
      return response;
    }

    // validate rules
    const validatedData = validateData(decodedData.allocations);
    if (validatedData.errors && validatedData.errors.length > 0) {
      const response: PayloadAction<string[]> = yield put(
        importWeightedAllocationsAction.rejected(validatedData.errors)
      );
      return response;
    }

    const convertedAllocations = validatedData.allocations.map((allocation: WeightedAssetAllocation) => ({
      code: allocation.securityCode,
      targetAllocation: allocation.targetAllocation,
    }));

    // send service
    const modelId: number = yield select(selectModelId);
    const payloadRequest: SaveModelCompositionPayload = {
      modelId,
      modelMethod: ModelCompositionMethod.Weighted,
      modelType: ModelCompositionType.SMA,
      assetAllocations: convertedAllocations,
    };
    const saveModelCompositionsResponse: SaveModelCompositionResult = yield call(saveModelCompositions, payloadRequest);

    // errors getting securities for afsl
    if (saveModelCompositionsResponse.errors && saveModelCompositionsResponse.errors.length > 0) {
      const response: PayloadAction<string[]> = yield put(
        importWeightedAllocationsAction.rejected(saveModelCompositionsResponse.errors)
      );
      return response;
    }

    // send response along with warnings and information
    const payload = {
      allocations: validatedData.allocations,
      warnings: saveModelCompositionsResponse.warnings,
      information: saveModelCompositionsResponse.information,
      errors: saveModelCompositionsResponse.errors,
    };

    const response: PayloadAction<AllocationsImporterResponse> = yield put(
      importWeightedAllocationsAction.fulfilled(payload)
    );
    return response;
  } catch (e) {
    const error = [`${e.status} - ${e.statusText}`];

    yield put(importWeightedAllocationsAction.rejected(error));
  }
}

export function* importWeightedAllocationsSaga(): Generator<ForkEffect<never>, void, unknown> {
  yield takeLatest(importWeightedAllocationsAction.pending, importAllocations);
}

function convertToCamelCase(str: string): string {
  return str.charAt(0).toLowerCase() + str.slice(1);
}
